import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  Box,
  Clock,
  GitBranch,
  Layers,
  Search as SearchIcon,
  Terminal,
  Zap,
} from "lucide-react";
import { api } from "@/api/client";
import { ResourceTree } from "@/components/ResourceTree";
import { LogsViewer } from "@/features/logs/LogsViewer";
import { AppStatusHeader } from "@/features/app-detail/AppStatusHeader";
import { PodsCard } from "@/features/app-detail/PodsCard";
import { SourceCard } from "@/features/app-detail/SourceCard";
import { formatRelative } from "@/features/app-detail/time";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn, Input, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, ToggleGroup, ToggleGroupItem } from "@fox1ck-org/ui";

type EventFilter = "all" | "warning" | "normal";

export default function AppDetail() {
  const { name } = useParams<{ name: string }>();

  const {
    data: app,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["cluster-app", name],
    queryFn: () => api.cluster.getApp(name!),
    enabled: !!name,
    refetchInterval: 15_000,
  });

  const { data: status } = useQuery({
    queryKey: ["cluster-app-status", name],
    queryFn: () => api.cluster.getStatus(name!),
    enabled: !!name,
    refetchInterval: 10_000,
  });

  const { data: history } = useQuery({
    queryKey: ["cluster-app-history", name],
    queryFn: () => api.cluster.getHistory(name!),
    enabled: !!name,
  });

  const { data: events } = useQuery({
    queryKey: ["cluster-app-events", name],
    queryFn: () => api.cluster.getEvents(name!),
    enabled: !!name,
    refetchInterval: 30_000,
  });

  const [eventFilter, setEventFilter] = useState<EventFilter>("all");
  const [eventQuery, setEventQuery] = useState("");

  const filteredEvents = useMemo(() => {
    const all = events?.events ?? [];
    return all
      .filter((e) => {
        if (eventFilter === "warning") return e.type === "Warning";
        if (eventFilter === "normal") return e.type !== "Warning";
        return true;
      })
      .filter((e) => {
        if (!eventQuery) return true;
        const needle = eventQuery.toLowerCase();
        return (
          e.message.toLowerCase().includes(needle) ||
          e.reason.toLowerCase().includes(needle)
        );
      });
  }, [events, eventFilter, eventQuery]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="flex-1">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-40 mt-2" />
          </div>
          <Skeleton className="h-9 w-72" />
        </div>
        <Skeleton className="h-10 w-96" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 w-full lg:col-span-2" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
        Не удалось загрузить приложение: {(error as Error)?.message ?? "Приложение не найдено"}
      </div>
    );
  }

  const pods = status?.pods ?? [];
  const deployment = status?.deployment;

  return (
    <div className="space-y-6">
      <AppStatusHeader app={app} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <Layers className="h-4 w-4 mr-1.5" />
            Обзор
          </TabsTrigger>
          <TabsTrigger value="logs">
            <Terminal className="h-4 w-4 mr-1.5" />
            Журналы
          </TabsTrigger>
          <TabsTrigger value="events">
            <Zap className="h-4 w-4 mr-1.5" />
            События
            {events?.events && events.events.length > 0 && (
              <Badge variant="neutral" className="ml-1.5 text-[10px] h-4 px-1">
                {events.events.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="h-4 w-4 mr-1.5" />
            История
          </TabsTrigger>
          <TabsTrigger value="gitops">
            <GitBranch className="h-4 w-4 mr-1.5" />
            GitOps
          </TabsTrigger>
        </TabsList>

        {/* ----------------- Overview ----------------- */}
        <TabsContent value="overview" className="space-y-6">
          {/* Top mini-stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat
              label="Готовых подов"
              value={
                deployment
                  ? `${deployment.readyReplicas} / ${deployment.replicas}`
                  : `${pods.filter((p) => p.ready).length} / ${pods.length}`
              }
              tone={
                deployment && deployment.readyReplicas < deployment.replicas
                  ? "warning"
                  : "default"
              }
              icon={<Box className="h-4 w-4" />}
            />
            <MiniStat
              label="Перезапуски"
              value={pods.reduce((sum, p) => sum + (p.restarts ?? 0), 0)}
              tone={
                pods.reduce((sum, p) => sum + (p.restarts ?? 0), 0) > 0
                  ? "warning"
                  : "default"
              }
              icon={<Activity className="h-4 w-4" />}
            />
            <MiniStat
              label="Волна синхронизации"
              value={
                app.conditions?.length ? `условий: ${app.conditions.length}` : "—"
              }
              icon={<GitBranch className="h-4 w-4" />}
            />
            <MiniStat
              label="Сверено"
              value={formatRelative(app.reconciledAt)}
              icon={<Clock className="h-4 w-4" />}
            />
          </div>

          {/* Conditions banner */}
          {app.conditions && app.conditions.length > 0 && (
            <div className="space-y-2">
              {app.conditions.map((cond, i) => (
                <div
                  key={i}
                  className="rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-2 text-sm text-yellow-400 flex items-start gap-2"
                >
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>{cond.type}:</strong> {cond.message}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Дерево ресурсов</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResourceTree nodes={app.resourceTree} />
                </CardContent>
              </Card>

              <SourceCard app={app} />
            </div>

            <div className="space-y-6">
              <PodsCard pods={pods} />
            </div>
          </div>
        </TabsContent>

        {/* ----------------- Logs ----------------- */}
        <TabsContent value="logs">
          <LogsViewer
            appName={name}
            namespace={app.namespace}
            defaultMode="live"
          />
        </TabsContent>

        {/* ----------------- Events ----------------- */}
        <TabsContent value="events">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-sm">События Kubernetes</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={eventQuery}
                      onChange={(e) => setEventQuery(e.target.value)}
                      placeholder="Фильтр событий…"
                      className="pl-8 h-7 text-xs w-[200px]"
                    />
                  </div>
                  <ToggleGroup
                    type="single"
                    size="sm"
                    value={eventFilter}
                    onValueChange={(v) => v && setEventFilter(v as EventFilter)}
                  >
                    <ToggleGroupItem value="all" className="h-7 px-2 text-[11px]">
                      Все
                    </ToggleGroupItem>
                    <ToggleGroupItem value="warning" className="h-7 px-2 text-[11px]">
                      Предупреждения
                    </ToggleGroupItem>
                    <ToggleGroupItem value="normal" className="h-7 px-2 text-[11px]">
                      Обычные
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {events?.events?.length ? "По фильтру событий нет" : "Событий нет"}
                </p>
              ) : (
                <ul className="space-y-2">
                  {filteredEvents.map((event, i) => (
                    <li
                      key={i}
                      className={cn(
                        "flex items-start gap-3 text-sm rounded-md border px-3 py-2",
                        event.type === "Warning"
                          ? "border-yellow-500/30 bg-yellow-500/5"
                          : "border-border bg-card"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1.5 h-2 w-2 rounded-full shrink-0",
                          event.type === "Warning" ? "bg-yellow-400" : "bg-blue-400"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant={event.type === "Warning" ? "warn" : "info"}
                            className="text-[10px]"
                          >
                            {event.reason}
                          </Badge>
                          {event.count > 1 && (
                            <span className="text-xs text-muted-foreground">
                              ×{event.count}
                            </span>
                          )}
                        </div>
                        <p className="text-muted-foreground mt-1 break-words">
                          {event.message}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                        {formatRelative(event.lastTimestamp)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----------------- History ----------------- */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">История синхронизаций</CardTitle>
            </CardHeader>
            <CardContent>
              {!history?.history?.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Истории синхронизаций нет
                </p>
              ) : (
                <ol className="relative">
                  <div className="absolute left-[14px] top-3 bottom-3 w-px bg-border" />
                  {history.history.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-start gap-3 relative pl-1 pb-4 last:pb-0"
                    >
                      <div className="h-7 w-7 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 flex items-center justify-center z-10 shrink-0">
                        <div className="h-2 w-2 rounded-full bg-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0 pt-1">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="font-mono text-sm text-foreground">
                            {entry.revision?.slice(0, 7) ?? "—"}
                          </span>
                          {entry.source?.path && (
                            <span className="text-muted-foreground text-xs font-mono">
                              {entry.source.path}
                            </span>
                          )}
                          {entry.source?.targetRevision && (
                            <Badge variant="neutral" className="text-[10px]">
                              {entry.source.targetRevision}
                            </Badge>
                          )}
                        </div>
                        <div className="text-muted-foreground text-xs mt-0.5">
                          deployed {formatRelative(entry.deployedAt)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----------------- GitOps ----------------- */}
        <TabsContent value="gitops">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Действия GitOps</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Изменения конфигурации идут через pull request в репозиторий инфраструктуры.
              </p>
              <div className="flex gap-3 flex-wrap">
                <Button asChild>
                  <Link to={`/apps/${name}/gitops?action=scale`}>Изменить реплики</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/apps/${name}/gitops?action=resources`}>Изменить ресурсы</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/apps/${name}/gitops?action=env`}>Изменить переменные</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniStat({
  label,
  value,
  tone = "default",
  icon,
}: {
  label: string;
  value: React.ReactNode;
  tone?: "default" | "warning" | "destructive" | "success";
  icon?: React.ReactNode;
}) {
  return (
    <Card
      className={cn(
        tone === "warning" && "border-yellow-500/40",
        tone === "destructive" && "border-destructive/50",
        tone === "success" && "border-emerald-500/40"
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        <div
          className={cn(
            "text-lg font-semibold mt-1",
            tone === "warning" && "text-yellow-400",
            tone === "destructive" && "text-destructive",
            tone === "success" && "text-emerald-400"
          )}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
